import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import _ from 'lodash';
import firebase from 'firebase/compat/app';

import AccountPickerContext from 'contexts/AccountPickerContext';
import VisualisationCacheContext, {
  EMPTY_VIS_CACHE,
  VisCache,
} from 'contexts/VisualisationCacheContext';
import useLockedDebouncedEffect from 'hooks/useLockedDebouncedEffect';
import captureException from 'services/captureException';
import STORE from 'store';
import retryOnce from 'utils/retryOnce';

const BATCH_SIZE = 30;

// A batch that can't be fetched (after one retry) has its ids added to
// failedIds, so they aren't taken for gadgets that don't exist
const batchFetchDocs = <T extends { id: string }>({
  collectionRef,
  ids,
  filterHidden,
  target,
  failedIds,
  errorName,
}: {
  collectionRef: firebase.firestore.CollectionReference<T>;
  ids: string[];
  filterHidden: boolean;
  target: T[];
  failedIds: string[];
  errorName: string;
}): Promise<void>[] => {
  if (ids.length === 0) return [];
  const docId = firebase.firestore.FieldPath.documentId();
  return _.chunk(ids, BATCH_SIZE).map((batch) =>
    retryOnce({ run: () => collectionRef.where(docId, 'in', batch).get() })
      .then((snapshot) => {
        snapshot.docs.forEach((doc) => {
          const data = doc.data();
          if (filterHidden && 'isHidden' in data && data.isHidden) return;
          target.push(data);
        });
      })
      .catch((e: unknown) => {
        const error = e instanceof Error ? e : new Error(String(e));
        error.name = errorName;
        captureException(error);
        failedIds.push(...batch);
      }),
  );
};

const fetchAllDocs = <T extends { id: string }>({
  collectionRef,
  filterHidden,
  target,
  errorName,
}: {
  collectionRef: firebase.firestore.CollectionReference<T>;
  filterHidden: boolean;
  target: T[];
  errorName: string;
}): Promise<void> =>
  collectionRef
    .get()
    .then((snapshot) => {
      snapshot.docs.forEach((doc) => {
        const data = doc.data();
        if (filterHidden && 'isHidden' in data && data.isHidden) return;
        target.push(data);
      });
    })
    .catch((e: unknown) => {
      const error = e instanceof Error ? e : new Error(String(e));
      error.name = errorName;
      captureException(error);
    });

const surgicalRefetchDocs = <T extends { id: string }>({
  collectionRef,
  ids,
  filterHidden,
  getMap,
  setMap,
  setCache,
  errorName,
}: {
  collectionRef: firebase.firestore.CollectionReference<T>;
  ids: string[];
  filterHidden: boolean;
  getMap: (cache: VisCache) => Map<string, T>;
  setMap: (cache: VisCache, map: Map<string, T>) => VisCache;
  setCache: React.Dispatch<React.SetStateAction<VisCache>>;
  errorName: string;
}): Promise<void>[] => {
  if (ids.length === 0) return [];
  const docId = firebase.firestore.FieldPath.documentId();
  return _.chunk(ids, BATCH_SIZE).map((batch) =>
    collectionRef
      .where(docId, 'in', batch)
      .get()
      .then((snap) => {
        const fetched = new Map<string, T>();
        snap.docs.forEach((doc) => {
          const d = doc.data();
          if (filterHidden && 'isHidden' in d && d.isHidden) return;
          fetched.set(d.id, d);
        });
        setCache((prev) => {
          const next = new Map(getMap(prev));
          batch.forEach((id) => {
            const fresh = fetched.get(id);
            if (fresh) {
              next.set(id, fresh);
            } else {
              next.delete(id);
            }
          });
          return setMap(prev, next);
        });
      })
      .catch((e: unknown) => {
        const error = e instanceof Error ? e : new Error(String(e));
        error.name = errorName;
        captureException(error);
      }),
  );
};

interface PendingIds {
  dashboardGadgetIds: string[];
  reportGadgetIds: string[];
}

const EMPTY_PENDING: PendingIds = {
  dashboardGadgetIds: [],
  reportGadgetIds: [],
};

const subtractIds = ({
  prev,
  toRemove,
}: {
  prev: string[];
  toRemove: Set<string>;
}): string[] => {
  const filtered = prev.filter((id) => !toRemove.has(id));
  return filtered.length === prev.length ? prev : filtered;
};

const mergeMap = <T extends { id: string }>({
  existing,
  items,
}: {
  existing: Map<string, T>;
  items: T[];
}): Map<string, T> => {
  if (items.length === 0) return existing;
  const next = new Map(existing);
  items.forEach((item) => next.set(item.id, item));
  return next;
};

const VisualisationCacheProvider = ({ children }: { children: ReactNode }) => {
  const { selectedAccountId } = useContext(AccountPickerContext);

  const [pendingIds, setPendingIds] = useState<PendingIds>(EMPTY_PENDING);
  const [cache, setCache] = useState<VisCache>(EMPTY_VIS_CACHE);
  const cacheRef = useRef(cache);
  cacheRef.current = cache;
  // Gadgets whose fetch failed (dashboard and report gadgets): resolved, so a
  // view doesn't wait on them forever, but not missing. A later request for
  // them tries again
  const [failedGadgetIds, setFailedGadgetIds] = useState<Set<string>>(
    new Set(),
  );
  const [notFoundGadgetIds, setNotFoundGadgetIds] = useState<Set<string>>(
    new Set(),
  );
  const [allGadgetsRequested, setAllGadgetsRequested] = useState(false);
  const allGadgetsRequestedRef = useRef(allGadgetsRequested);
  allGadgetsRequestedRef.current = allGadgetsRequested;
  const [allReportGadgetsRequested, setAllReportGadgetsRequested] =
    useState(false);
  const allReportGadgetsRequestedRef = useRef(allReportGadgetsRequested);
  allReportGadgetsRequestedRef.current = allReportGadgetsRequested;

  useEffect(() => {
    return () => {
      setPendingIds(EMPTY_PENDING);
      setCache(EMPTY_VIS_CACHE);
      setNotFoundGadgetIds(new Set());
      setFailedGadgetIds(new Set());
      setAllGadgetsRequested(false);
      setAllGadgetsLoaded(false);
      setAllReportGadgetsRequested(false);
      setAllReportGadgetsLoaded(false);
    };
  }, [selectedAccountId]);

  // Listen to cache version for invalidation.
  const isFirstVisSnapshot = useRef(true);
  useEffect(() => {
    isFirstVisSnapshot.current = true;
    const unsubscribe = STORE.visualisations
      .getVisCacheVersionRef({ accountId: selectedAccountId })
      .onSnapshot((snapshot) => {
        if (isFirstVisSnapshot.current) {
          isFirstVisSnapshot.current = false;
          return;
        }

        if (!snapshot.exists) {
          return;
        }

        const data = snapshot.data() as
          | {
              changes?: Partial<Record<keyof VisCache, string[]>>;
            }
          | undefined;

        const changes = data?.changes;

        if (!changes) {
          setCache(EMPTY_VIS_CACHE);
          setPendingIds(EMPTY_PENDING);
          return;
        }

        const accountId = selectedAccountId;
        const currentCache = cacheRef.current;

        const filterCachedIds = ({
          ids,
          cacheKey,
          allRequested,
        }: {
          ids: string[] | undefined;
          cacheKey: keyof VisCache;
          allRequested: boolean;
        }): string[] =>
          (ids || []).filter(
            (id) => currentCache[cacheKey].has(id) || allRequested,
          );

        const refetchPromises: Promise<void>[] = [
          ...surgicalRefetchDocs({
            collectionRef: STORE.visualisations.getDashboardGadgetsRef({
              accountId,
            }),
            ids: filterCachedIds({
              ids: changes.dashboardGadgets,
              cacheKey: 'dashboardGadgets',
              allRequested: allGadgetsRequestedRef.current,
            }),
            filterHidden: false,
            getMap: (c) => c.dashboardGadgets,
            setMap: (c, m) => ({ ...c, dashboardGadgets: m }),
            setCache,
            errorName:
              'VisualisationCacheProvider - surgical refetch dashboardGadgets failed',
          }),
          ...surgicalRefetchDocs({
            collectionRef: STORE.visualisations.getReportGadgetsRef({
              accountId,
            }),
            ids: filterCachedIds({
              ids: changes.reportGadgets,
              cacheKey: 'reportGadgets',
              allRequested: allReportGadgetsRequestedRef.current,
            }),
            filterHidden: false,
            getMap: (c) => c.reportGadgets,
            setMap: (c, m) => ({ ...c, reportGadgets: m }),
            setCache,
            errorName:
              'VisualisationCacheProvider - surgical refetch reportGadgets failed',
          }),
        ];

        Promise.all(refetchPromises).catch(() => {});
      });

    return () => {
      unsubscribe();
    };
  }, [selectedAccountId]);

  // --- On-demand fetch ---

  const fetchArgs = useMemo(() => {
    const isGadgetResolved = (id: string) =>
      cache.dashboardGadgets.has(id) || notFoundGadgetIds.has(id);

    const dashboardGadgetIds = _.uniq(
      pendingIds.dashboardGadgetIds.filter((id) => !isGadgetResolved(id)),
    );
    const reportGadgetIds = _.uniq(
      pendingIds.reportGadgetIds.filter((id) => !cache.reportGadgets.has(id)),
    );

    if (dashboardGadgetIds.length === 0 && reportGadgetIds.length === 0)
      return undefined;
    return {
      dashboardGadgetIds,
      reportGadgetIds,
      accountId: selectedAccountId,
    };
  }, [pendingIds, cache, notFoundGadgetIds, selectedAccountId]);

  const fetchCallback = useCallback(
    async (args: {
      dashboardGadgetIds: string[];
      reportGadgetIds: string[];
      accountId: string;
    }) => {
      const dashboardGadgets: DashboardGadget[] = [];
      const reportGadgets: ReportGadgets.ReportGadget[] = [];
      const failedIds: string[] = [];

      await Promise.all([
        ...batchFetchDocs({
          collectionRef: STORE.visualisations.getDashboardGadgetsRef({
            accountId: args.accountId,
          }),
          ids: args.dashboardGadgetIds,
          filterHidden: false,
          target: dashboardGadgets,
          failedIds,
          errorName:
            'VisualisationCacheProvider - dashboardGadgets batch fetch failed',
        }),
        ...batchFetchDocs({
          collectionRef: STORE.visualisations.getReportGadgetsRef({
            accountId: args.accountId,
          }),
          ids: args.reportGadgetIds,
          filterHidden: false,
          target: reportGadgets,
          failedIds,
          errorName:
            'VisualisationCacheProvider - reportGadgets batch fetch failed',
        }),
      ]);

      return {
        dashboardGadgets,
        reportGadgets,
        failedIds,
        queriedDashboardGadgetIds: args.dashboardGadgetIds,
        queriedReportGadgetIds: args.reportGadgetIds,
      };
    },
    [],
  );

  const fetchResponseHandler = useCallback(
    ({
      dashboardGadgets,
      reportGadgets,
      failedIds,
      queriedDashboardGadgetIds,
      queriedReportGadgetIds,
    }: {
      dashboardGadgets: DashboardGadget[];
      reportGadgets: ReportGadgets.ReportGadget[];
      failedIds: string[];
      queriedDashboardGadgetIds: string[];
      queriedReportGadgetIds: string[];
    }) => {
      const foundGadgetIds = new Set(dashboardGadgets.map((g) => g.id));
      const failed = new Set(failedIds);
      // A gadget whose batch failed may well exist: it's failed, not missing
      const newNotFoundGadgetIds = queriedDashboardGadgetIds.filter(
        (id) => !foundGadgetIds.has(id) && !failed.has(id),
      );
      if (failed.size > 0) {
        setFailedGadgetIds((prev) => new Set([...prev, ...failed]));
      }
      if (newNotFoundGadgetIds.length > 0) {
        captureException(
          new Error(
            `VisualisationCacheProvider - dashboard gadgets not found: ${newNotFoundGadgetIds.join(', ')}`,
          ),
        );
        setNotFoundGadgetIds((prev) => {
          const next = new Set(prev);
          newNotFoundGadgetIds.forEach((id) => next.add(id));
          return next;
        });
      }
      setCache((prev) => ({
        ...prev,
        dashboardGadgets: mergeMap({
          existing: prev.dashboardGadgets,
          items: dashboardGadgets,
        }),
        reportGadgets: mergeMap({
          existing: prev.reportGadgets,
          items: reportGadgets,
        }),
      }));
      setPendingIds((prev) => {
        const gadgetSet = new Set(queriedDashboardGadgetIds);
        const reportGadgetSet = new Set(queriedReportGadgetIds);
        return {
          ...prev,
          dashboardGadgetIds: subtractIds({
            prev: prev.dashboardGadgetIds,
            toRemove: gadgetSet,
          }),
          reportGadgetIds: subtractIds({
            prev: prev.reportGadgetIds,
            toRemove: reportGadgetSet,
          }),
        };
      });
    },
    [],
  );

  useLockedDebouncedEffect({
    args: fetchArgs,
    callback: fetchCallback,
    responseHandler: fetchResponseHandler,
  });

  // --- Request all ---

  const [allGadgetsLoaded, setAllGadgetsLoaded] = useState(false);
  const [allReportGadgetsLoaded, setAllReportGadgetsLoaded] = useState(false);

  const requestAllArgs = useMemo(() => {
    if (!allGadgetsRequested && !allReportGadgetsRequested) return undefined;
    return {
      allGadgetsRequested,
      allReportGadgetsRequested,
      accountId: selectedAccountId,
    };
  }, [allGadgetsRequested, allReportGadgetsRequested, selectedAccountId]);

  const requestAllCallback = useCallback(
    async (args: {
      allGadgetsRequested: boolean;
      allReportGadgetsRequested: boolean;
      accountId: string;
    }): Promise<{
      dashboardGadgets: DashboardGadget[];
      reportGadgets: ReportGadgets.ReportGadget[];
    }> => {
      const dashboardGadgets: DashboardGadget[] = [];
      const reportGadgets: ReportGadgets.ReportGadget[] = [];

      const promises: Promise<void>[] = [];

      if (args.allGadgetsRequested) {
        promises.push(
          fetchAllDocs({
            collectionRef: STORE.visualisations.getDashboardGadgetsRef({
              accountId: args.accountId,
            }),
            filterHidden: false,
            target: dashboardGadgets,
            errorName: 'VisualisationCacheProvider - requestAll gadgets failed',
          }),
        );
      }

      if (args.allReportGadgetsRequested) {
        promises.push(
          fetchAllDocs({
            collectionRef: STORE.visualisations.getReportGadgetsRef({
              accountId: args.accountId,
            }),
            filterHidden: false,
            target: reportGadgets,
            errorName:
              'VisualisationCacheProvider - requestAll report gadgets failed',
          }),
        );
      }

      await Promise.all(promises);

      return { dashboardGadgets, reportGadgets };
    },
    [],
  );

  const requestAllResponseHandler = useCallback(
    (response: {
      dashboardGadgets: DashboardGadget[];
      reportGadgets: ReportGadgets.ReportGadget[];
    }) => {
      setCache((prev) => ({
        ...prev,
        dashboardGadgets: mergeMap({
          existing: prev.dashboardGadgets,
          items: response.dashboardGadgets,
        }),
        reportGadgets: mergeMap({
          existing: prev.reportGadgets,
          items: response.reportGadgets,
        }),
      }));
      if (allGadgetsRequested) setAllGadgetsLoaded(true);
      if (allReportGadgetsRequested) setAllReportGadgetsLoaded(true);
    },
    [allGadgetsRequested, allReportGadgetsRequested],
  );

  useLockedDebouncedEffect({
    args: requestAllArgs,
    callback: requestAllCallback,
    responseHandler: requestAllResponseHandler,
  });

  // --- Request functions ---

  // Asking for gadgets again retries any whose fetch failed
  const retryFailed = useCallback((ids: string[]) => {
    setFailedGadgetIds((prev) =>
      ids.some((id) => prev.has(id))
        ? new Set([...prev].filter((id) => !ids.includes(id)))
        : prev,
    );
  }, []);

  const requestDashboardGadgetsByIds = useCallback(
    (ids: string[]) => {
      retryFailed(ids);
      setPendingIds((prev) => {
        const next = _.uniq([...prev.dashboardGadgetIds, ...ids]);
        return _.isEqual(next, prev.dashboardGadgetIds)
          ? prev
          : { ...prev, dashboardGadgetIds: next };
      });
    },
    [retryFailed],
  );

  const requestAllDashboardGadgets = useCallback(() => {
    setAllGadgetsRequested(true);
  }, []);

  const isGadgetIdResolved = useCallback(
    (id: string) =>
      cache.dashboardGadgets.has(id) ||
      notFoundGadgetIds.has(id) ||
      failedGadgetIds.has(id),
    [cache, notFoundGadgetIds, failedGadgetIds],
  );

  const isGadgetIdFailed = useCallback(
    (id: string) => failedGadgetIds.has(id),
    [failedGadgetIds],
  );

  const isGadgetIdNotFound = useCallback(
    (id: string) => notFoundGadgetIds.has(id),
    [notFoundGadgetIds],
  );

  const requestReportGadgetsByIds = useCallback(
    (ids: string[]) => {
      retryFailed(ids);
      setPendingIds((prev) => {
        const next = _.uniq([...prev.reportGadgetIds, ...ids]);
        return _.isEqual(next, prev.reportGadgetIds)
          ? prev
          : { ...prev, reportGadgetIds: next };
      });
    },
    [retryFailed],
  );

  const requestAllReportGadgets = useCallback(() => {
    setAllReportGadgetsRequested(true);
  }, []);

  const isReportGadgetIdResolved = useCallback(
    (id: string) => cache.reportGadgets.has(id) || failedGadgetIds.has(id),
    [cache, failedGadgetIds],
  );

  const putReportGadgetInCache = useCallback(
    (gadget: ReportGadgets.ReportGadget) => {
      setCache((prev) => ({
        ...prev,
        reportGadgets: new Map(prev.reportGadgets).set(gadget.id, gadget),
      }));
    },
    [],
  );

  const value = useMemo(
    () => ({
      cache,
      requestDashboardGadgetsByIds,
      requestAllDashboardGadgets,
      allGadgetsLoaded,
      isGadgetIdResolved,
      isGadgetIdNotFound,
      isGadgetIdFailed,
      requestReportGadgetsByIds,
      requestAllReportGadgets,
      allReportGadgetsLoaded,
      isReportGadgetIdResolved,
      putReportGadgetInCache,
    }),
    [
      cache,
      requestDashboardGadgetsByIds,
      requestAllDashboardGadgets,
      allGadgetsLoaded,
      isGadgetIdResolved,
      isGadgetIdNotFound,
      isGadgetIdFailed,
      requestReportGadgetsByIds,
      requestAllReportGadgets,
      allReportGadgetsLoaded,
      isReportGadgetIdResolved,
      putReportGadgetInCache,
    ],
  );

  return (
    <VisualisationCacheContext.Provider value={value}>
      {children}
    </VisualisationCacheContext.Provider>
  );
};

export default VisualisationCacheProvider;
